import { forwardRef } from 'react';
import type { AppIconComponent, AppIconProps } from './app-icon';
import { MingcuteLineAssetIcon } from './mingcute-line-asset-icon';
import type { MingcuteLineIconKey } from './mingcute-line-icon-catalog.generated';

function createAppIconAssetComponent(
  iconKey: MingcuteLineIconKey,
  displayName: string
): AppIconComponent {
  const Component = forwardRef<SVGSVGElement, AppIconProps>((props, ref) => (
    <MingcuteLineAssetIcon iconKey={iconKey} ref={ref} {...props} />
  ));
  Component.displayName = displayName;
  return Component;
}

export const AlertCircle = createAppIconAssetComponent(
  'warning',
  'AlertCircle'
);
export const ArrowDown = createAppIconAssetComponent('arrow-down', 'ArrowDown');
export const ArrowLeft = createAppIconAssetComponent('arrow-left', 'ArrowLeft');
export const ArrowRight = createAppIconAssetComponent(
  'arrow-right',
  'ArrowRight'
);
export const ArrowUp = createAppIconAssetComponent('arrow-up', 'ArrowUp');
export const ArrowUpDown = createAppIconAssetComponent(
  'transfer-vertical',
  'ArrowUpDown'
);
export const ArrowUpRight = createAppIconAssetComponent(
  'arrow-right-up',
  'ArrowUpRight'
);
export const Award = createAppIconAssetComponent('award', 'Award');
export const Banknote = createAppIconAssetComponent('bill', 'Banknote');
export const BarChart3 = createAppIconAssetComponent('chart-bar', 'BarChart3');
export const Book = createAppIconAssetComponent('book-2', 'Book');
export const Blocks = createAppIconAssetComponent('classify-2', 'Blocks');
export const Bot = createAppIconAssetComponent('robot', 'Bot');
export const Bug = createAppIconAssetComponent('bug', 'Bug');
export const Calendar = createAppIconAssetComponent('calendar-2', 'Calendar');
export const Camera = createAppIconAssetComponent('camera-2', 'Camera');
export const ChartColumn = createAppIconAssetComponent(
  'chart-vertical',
  'ChartColumn'
);
export const Check = createAppIconAssetComponent('check', 'Check');
export const CheckCircle = createAppIconAssetComponent(
  'check-circle',
  'CheckCircle'
);
export const CheckCircle2 = createAppIconAssetComponent(
  'check-circle',
  'CheckCircle2'
);
export const CheckSquare = createAppIconAssetComponent(
  'checkbox',
  'CheckSquare'
);
export const ChevronDown = createAppIconAssetComponent('down', 'ChevronDown');
export const ChevronLeft = createAppIconAssetComponent('left', 'ChevronLeft');
export const ChevronRight = createAppIconAssetComponent(
  'right',
  'ChevronRight'
);
export const ChevronsLeft = createAppIconAssetComponent(
  'arrows-left',
  'ChevronsLeft'
);
export const ChevronsRight = createAppIconAssetComponent(
  'arrows-right',
  'ChevronsRight'
);
export const ChevronsUpDown = createAppIconAssetComponent(
  'transfer-vertical',
  'ChevronsUpDown'
);
export const ChevronUp = createAppIconAssetComponent('up', 'ChevronUp');
export const CircleCheck = createAppIconAssetComponent(
  'check-circle',
  'CircleCheck'
);
export const CircleHelp = createAppIconAssetComponent('question', 'CircleHelp');
export const CircleUserRound = createAppIconAssetComponent(
  'user-4',
  'CircleUserRound'
);
export const Clapperboard = createAppIconAssetComponent(
  'clapperboard',
  'Clapperboard'
);
export const Clock = createAppIconAssetComponent('time', 'Clock');
export const Clock3 = createAppIconAssetComponent('clock', 'Clock3');
export const CloudUpload = createAppIconAssetComponent(
  'upload-3',
  'CloudUpload'
);
export const Coins = createAppIconAssetComponent('coin-2', 'Coins');
export const Copy = createAppIconAssetComponent('copy-2', 'Copy');
export const Cpu = createAppIconAssetComponent('chip', 'Cpu');
export const CreditCard = createAppIconAssetComponent(
  'bank-card',
  'CreditCard'
);
export const Crown = createAppIconAssetComponent('vip-2', 'Crown');
export const Download = createAppIconAssetComponent('download-2', 'Download');
export const Eraser = createAppIconAssetComponent('eraser', 'Eraser');
export const Eye = createAppIconAssetComponent('eye-2', 'Eye');
export const EyeOff = createAppIconAssetComponent('eye-close', 'EyeOff');
export const FileDown = createAppIconAssetComponent(
  'file-download',
  'FileDown'
);
export const FileImage = createAppIconAssetComponent('pic-2', 'FileImage');
export const FileJson = createAppIconAssetComponent('file-code', 'FileJson');
export const FileText = createAppIconAssetComponent('file', 'FileText');
export const FileUp = createAppIconAssetComponent('file-upload', 'FileUp');
export const Filter = createAppIconAssetComponent('filter-3', 'Filter');
export const Gift = createAppIconAssetComponent('gift-2', 'Gift');
export const Globe = createAppIconAssetComponent('world-2', 'Globe');
export const Grid = createAppIconAssetComponent('grid', 'Grid');
export const Grid2x2 = createAppIconAssetComponent('grid-2', 'Grid2x2');
export const GripVertical = createAppIconAssetComponent(
  'dots-vertical',
  'GripVertical'
);
export const HandCoins = createAppIconAssetComponent(
  'receive-money',
  'HandCoins'
);
export const History = createAppIconAssetComponent(
  'history-anticlockwise',
  'History'
);
export const Home = createAppIconAssetComponent('home-1', 'Home');
export const Image = createAppIconAssetComponent('pic-2', 'Image');
export const ImageUp = createAppIconAssetComponent('upload-3', 'ImageUp');
export const Info = createAppIconAssetComponent('information', 'Info');
export const Languages = createAppIconAssetComponent('translate', 'Languages');
export const Laptop = createAppIconAssetComponent('laptop-2', 'Laptop');
export const LaptopMinimal = createAppIconAssetComponent(
  'laptop-2',
  'LaptopMinimal'
);
export const Layers = createAppIconAssetComponent('layer', 'Layers');
export const Layers3 = createAppIconAssetComponent('layers', 'Layers3');
export const LayoutDashboard = createAppIconAssetComponent(
  'grid-2',
  'LayoutDashboard'
);
export const LayoutGrid = createAppIconAssetComponent('grid', 'LayoutGrid');
export const List = createAppIconAssetComponent('list-check-2', 'List');
export const Loader2 = createAppIconAssetComponent('loading-3', 'Loader2');
export const Lock = createAppIconAssetComponent('lock', 'Lock');
export const LogIn = createAppIconAssetComponent('enter-door', 'LogIn');
export const LogOut = createAppIconAssetComponent('exit', 'LogOut');
export const Mail = createAppIconAssetComponent('mail', 'Mail');
export const Maximize = createAppIconAssetComponent('fullscreen', 'Maximize');
export const Menu = createAppIconAssetComponent('menu', 'Menu');
export const MessageCircle = createAppIconAssetComponent(
  'message-3',
  'MessageCircle'
);
export const MicVocal = createAppIconAssetComponent('mic-2', 'MicVocal');
export const Minimize = createAppIconAssetComponent(
  'fullscreen-exit',
  'Minimize'
);
export const Monitor = createAppIconAssetComponent('computer', 'Monitor');
export const Moon = createAppIconAssetComponent('moon', 'Moon');
export const MoonStar = createAppIconAssetComponent('moon-stars', 'MoonStar');
export const More1 = createAppIconAssetComponent('more-1', 'More1');
export const MoreHorizontal = createAppIconAssetComponent(
  'dots',
  'MoreHorizontal'
);
export const MousePointerClick = createAppIconAssetComponent(
  'cursor-2',
  'MousePointerClick'
);
export const Package = createAppIconAssetComponent('package', 'Package');
export const Palette = createAppIconAssetComponent('palette-2', 'Palette');
export const PanelLeft = createAppIconAssetComponent(
  'layout-left',
  'PanelLeft'
);
export const Paperclip = createAppIconAssetComponent(
  'attachment-2',
  'Paperclip'
);
export const Pencil = createAppIconAssetComponent('edit-3', 'Pencil');
export const Play = createAppIconAssetComponent('play', 'Play');
export const Plus = createAppIconAssetComponent('add', 'Plus');
export const Quote = createAppIconAssetComponent('quote-left', 'Quote');
export const RefreshCw = createAppIconAssetComponent('refresh-2', 'RefreshCw');
export const RotateCcw = createAppIconAssetComponent(
  'refresh-anticlockwise-1',
  'RotateCcw'
);
export const Save = createAppIconAssetComponent('save-2', 'Save');
export const ScanEye = createAppIconAssetComponent('scan-2', 'ScanEye');
export const Search = createAppIconAssetComponent('search-2', 'Search');
export const Send = createAppIconAssetComponent('send-plane', 'Send');
export const Settings = createAppIconAssetComponent('settings-1', 'Settings');
export const Settings2 = createAppIconAssetComponent('settings-1', 'Settings2');
export const Share2 = createAppIconAssetComponent('share-2', 'Share2');
export const Shield = createAppIconAssetComponent('shield-shape', 'Shield');
export const ShieldAlert = createAppIconAssetComponent(
  'safe-alert',
  'ShieldAlert'
);
export const ShieldCheck = createAppIconAssetComponent(
  'safety-certificate',
  'ShieldCheck'
);
export const ShoppingCart = createAppIconAssetComponent(
  'shopping-cart-2',
  'ShoppingCart'
);
export const Smartphone = createAppIconAssetComponent('phone', 'Smartphone');
export const Sparkles = createAppIconAssetComponent('sparkles', 'Sparkles');
export const Star = createAppIconAssetComponent('star', 'Star');
export const Sun = createAppIconAssetComponent('sun', 'Sun');
export const SunMedium = createAppIconAssetComponent('sun-2', 'SunMedium');
export const Terminal = createAppIconAssetComponent('terminal', 'Terminal');
export const ThumbsDown = createAppIconAssetComponent(
  'thumb-down',
  'ThumbsDown'
);
export const ThumbsUp = createAppIconAssetComponent('thumb-up', 'ThumbsUp');
export const Trash2 = createAppIconAssetComponent('delete-2', 'Trash2');
export const TrendingDown = createAppIconAssetComponent(
  'trending-down',
  'TrendingDown'
);
export const TriangleAlert = createAppIconAssetComponent(
  'alert',
  'TriangleAlert'
);
export const Truck = createAppIconAssetComponent('truck', 'Truck');
export const Upload = createAppIconAssetComponent('upload-2', 'Upload');
export const User2 = createAppIconAssetComponent('user-3', 'User2');
export const User = createAppIconAssetComponent('user-3', 'User');
export const UserRound = createAppIconAssetComponent('user-4', 'UserRound');
export const UserRoundCheck = createAppIconAssetComponent(
  'user-follow',
  'UserRoundCheck'
);
export const UserRoundX = createAppIconAssetComponent('user-x', 'UserRoundX');
export const Users = createAppIconAssetComponent('group-2', 'Users');
export const UsersRound = createAppIconAssetComponent('group-2', 'UsersRound');
export const Video = createAppIconAssetComponent('video', 'Video');
export const Wand2 = createAppIconAssetComponent('magic-2', 'Wand2');
export const WandSparkles = createAppIconAssetComponent(
  'sparkles',
  'WandSparkles'
);
export const Wrench = createAppIconAssetComponent('tool', 'Wrench');
export const X = createAppIconAssetComponent('close', 'X');
export const XCircle = createAppIconAssetComponent('close-circle', 'XCircle');
export const Zap = createAppIconAssetComponent('lightning', 'Zap');

export const AlertCircleIcon = AlertCircle;
export const ArrowDownIcon = ArrowDown;
export const ArrowUpDownIcon = ArrowUpDown;
export const ArrowUpIcon = ArrowUp;
export const BanknoteIcon = Banknote;
export const CalendarIcon = Calendar;
export const CheckCircleIcon = CheckCircle;
export const ChevronDownIcon = ChevronDown;
export const ChevronLeftIcon = ChevronLeft;
export const ChevronRightIcon = ChevronRight;
export const ChevronsLeftIcon = ChevronsLeft;
export const ChevronsRightIcon = ChevronsRight;
export const ChevronsUpDownIcon = ChevronsUpDown;
export const ChevronUpIcon = ChevronUp;
export const ClockIcon = Clock;
export const CoinsIcon = Coins;
export const GiftIcon = Gift;
export const HandCoinsIcon = HandCoins;
export const InfoIcon = Info;
export const LaptopIcon = Laptop;
export const Loader2Icon = Loader2;
export const MoonIcon = Moon;
export const RefreshCwIcon = RefreshCw;
export const RotateCcwIcon = RotateCcw;
export const Settings2Icon = Settings2;
export const ShoppingCartIcon = ShoppingCart;
export const SparklesIcon = Sparkles;
export const SunIcon = Sun;
export const Trash2Icon = Trash2;
export const User2Icon = User2;
export const UserRoundCheckIcon = UserRoundCheck;
export const UserRoundXIcon = UserRoundX;
export const XCircleIcon = XCircle;

export type { AppIconComponent, AppIconProps } from './app-icon';
